// Display values for one putt. Ported from puttr-app/components/common/PuttCard.js
// (computePuttDisplay) and PuttReplay.js (computePathGeometry), synced 2026-09-11,
// so the shared web page shows the same grade, pace and entry text as the app.
import { primaryGreen, lightGreen, red } from '../../constants/Colors';
import { getHoleEntry, getTeeEntry, getMissResult, getEntryDescription, getPuttGrade } from './GameFunctions';
import { analyzeRollout, projectRollout, fmtRollout, getPuttScoreRollout } from './RolloutFunctions';

export function computePuttDisplay(putt) {
  let hb = putt.holeBounds;
  if (typeof hb === 'string') hb = JSON.parse(hb);

  let bp = putt.ballPath;
  if (typeof bp === 'string') bp = JSON.parse(bp);

  const made = putt.made == 1;

  // Pace: prefer the server-stored rollout (save-time / backfilled); compute
  // from the path otherwise. MySQL DECIMAL arrives as a string.
  const stored = Number(putt.rollout);
  const rollout = Number.isFinite(stored)
    ? stored
    : projectRollout(analyzeRollout(bp, hb), null, Number(putt.made) === 0 ? false : undefined);
  const paceText = fmtRollout(rollout) || 'no read';

  const holeEntry = getHoleEntry(bp, hb);
  const teeEntry = getTeeEntry(putt.distance, putt.tee);
  const angDiff = holeEntry[2] - teeEntry;
  const score = getPuttScoreRollout(angDiff, putt.made, rollout, 0);
  const grade = getPuttGrade(score);
  const roundedScore = Math.round(score);

  // Break label
  let breakLabel = 'Straight';
  if (putt.tee == 1) breakLabel = 'Left to right';
  if (putt.tee == 3) breakLabel = 'Right to left';

  // Entry or miss description
  let resultText = '';
  let resultColor = lightGreen;
  if (made) {
    resultText = getEntryDescription(angDiff);
    resultText = resultText.charAt(0) + resultText.slice(1).toLowerCase();
  } else {
    const missDir = getMissResult(bp, hb);
    resultText = 'Missed ' + missDir.toLowerCase();
    resultColor = red;
  }

  // Grade color
  let gradeColor = lightGreen;
  if (roundedScore < 20) gradeColor = red;
  else if (roundedScore < 50) gradeColor = red;
  else if (roundedScore < 70) gradeColor = '#fff';

  // Pace bar: rollout plotted from 15 in short to 30 in past. The ideal zone
  // (dies at the center up to a foot past) reads green; short reads blue,
  // long reads orange.
  const speedInIdealZone = rollout != null && rollout >= -2 && rollout <= 12;
  const speedBarColor = speedInIdealZone
    ? primaryGreen
    : (rollout != null && rollout < -2 ? '#5687FF' : '#FF9500');
  const speedBarPct = rollout == null ? 0.05 : Math.min(Math.max((rollout + 15) / 45, 0.05), 1);

  // Left accent color
  const accentColor = made ? primaryGreen : red;

  return {
    hb, bp, made, rollout, paceText, angDiff, score, grade, roundedScore,
    breakLabel, resultText, resultColor, gradeColor,
    speedBarColor, speedBarPct, accentColor,
  };
}

// Ball path geometry in a `width`-wide box (same maths as the app's PuttReplay).
export function computePathGeometry(d, width) {
  const { hb, bp } = d;
  const vizWidth = width - 32;
  const mult = vizWidth / 475;
  const xAdjust = 82.5 * mult;
  const gw = 475 * mult;
  const gh = 350 * mult;

  let realHoleX = 320;
  let realHoleY = 100;
  let realHoleRad = 60;
  let xHoleDiff = 0;

  if (hb) {
    realHoleX = hb[0];
    xHoleDiff = 320 - realHoleX;
    realHoleX += xHoleDiff;
    realHoleY = hb[1];
    realHoleRad = hb[2] / 2;
  }

  const holeX = (realHoleX * mult) - xAdjust;
  const holeY = realHoleY * mult;
  const holeRad = realHoleRad * mult;

  const points = [];
  const radii = [];
  if (bp && bp.length > 0) {
    for (let i = 0; i < bp.length; i++) {
      points.push([((bp[i][0] + xHoleDiff) * mult) - xAdjust, bp[i][1] * mult]);
      radii.push(bp[i][2] * mult);
    }
  }

  return { gw, gh, mult, xAdjust, xHoleDiff, holeX, holeY, holeRad, points, radii };
}
